import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    Grid,
    Avatar,
    Chip,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Snackbar,
    Alert,
    CircularProgress,
    Button,
    Stack,
    Badge
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    People as PeopleIcon,
    Groups as GroupsIcon,
    ExpandMore as ExpandMoreIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    CalendarToday as CalendarIcon,
    RemoveRedEye
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';

const Team = () => {
    const theme = useTheme();
    const [salesTeam, setSalesTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [expandedMember, setExpandedMember] = useState(null);
    const [subscriptionPackages, setSubscriptionPackages] = useState([]);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedMember(isExpanded ? panel : null);
    };

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const fetchSalesTeam = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('authToken');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/my-sales-teamcontracts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log("Sales Team API Full Response:", response.data);

            // Debug avatar URLs
            if (response.data.salesTeam) {
                response.data.salesTeam.forEach(member => {
                    console.log(`Member ${member.name} Details:`, {
                        id: member.id,
                        avatar: member.avatar,
                        fullUrl: member.avatar ? `${process.env.REACT_APP_API_URL}/uploads/avatars/${member.avatar}` : 'no avatar',
                        role: member.role,
                        email: member.email
                    });
                });
            }
            if (response.data.success) {
                setSalesTeam(response.data.salesTeam || []);
            } else {
                throw new Error(response.data.message || 'Failed to load team');
            }
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || 'Network error';
            setError(errorMsg);
            showSnackbar(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Fetch sales team and packages on mount
    useEffect(() => {
        const fetchPackages = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/subscription-packages`);
                setSubscriptionPackages(response.data);
            } catch (err) {
                console.error('Failed to fetch packages:', err);
            }
        };
        fetchSalesTeam();
        fetchPackages();
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box textAlign="center" p={4} height="80vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <PeopleIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" color="error" gutterBottom>
                    Failed to load team
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {error}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchSalesTeam}
                    sx={{ px: 4, py: 1 }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    background: theme.palette.primary.main,
                    color: 'common.white',
                    boxShadow: theme.shadows[4]
                }}
            >
                <Grid container alignItems="center" spacing={3}>
                    <Grid item>
                        <Avatar
                            sx={{
                                backgroundColor: 'common.white',
                                color: theme.palette.primary.main,
                                width: 64,
                                height: 64,
                                boxShadow: theme.shadows[2]
                            }}
                        >
                            <GroupsIcon fontSize="large" />
                        </Avatar>
                    </Grid>
                    <Grid item xs>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography variant="h4" fontWeight={600} gutterBottom>
                                    Sales Team Dashboard
                                </Typography>
                                <Stack direction="row" spacing={3}>
                                    <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <PeopleIcon sx={{ mr: 1, fontSize: '1rem' }} />
                                        {salesTeam.length} team members
                                    </Typography>
                                    <Typography variant="body1">
                                        Q2 2025 Performance
                                    </Typography>
                                </Stack>
                            </Box>
                            <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<RefreshIcon />}
                                onClick={fetchSalesTeam}
                                disabled={loading}
                                sx={{
                                    marginLeft: "480px",
                                    borderRadius: 1,
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                    color: 'common.white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        borderColor: 'common.white'
                                    }
                                }}
                            >
                                Refresh Data
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Team Members */}
            <Box>
                {salesTeam.length === 0 ? (
                    <Paper
                        elevation={0}
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 2,
                            border: `1px dashed ${theme.palette.divider}`
                        }}
                    >
                        <PeopleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Team Members Found
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            You currently have no sales persons assigned to your team.
                        </Typography>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={fetchSalesTeam}
                            sx={{ px: 4 }}
                        >
                            Refresh
                        </Button>
                    </Paper>
                ) : (
                    salesTeam.map(member => (
                        <Accordion
                            key={member._id}
                            expanded={expandedMember === member._id}
                            onChange={handleAccordionChange(member._id)}
                            sx={{
                                mb: 2,
                                borderRadius: '8px !important',
                                overflow: 'hidden',
                                boxShadow: theme.shadows[1],
                                '&:before': { display: 'none' },
                                '&.Mui-expanded': { boxShadow: theme.shadows[3] }
                            }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box display="flex" alignItems="center" width="100%">                                    <Badge
                                    overlap="circular"
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    variant="dot"
                                    color={member.status === 'active' ? 'success' : 'error'}
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                                        },
                                        mr: 3
                                    }}
                                >                                        <Avatar
                                        src={member.avatar ? `${process.env.REACT_APP_API_URL}/uploads/avatars/${member.avatar}` : null}
                                    alt={member.name}
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        bgcolor: !member.avatar || member.avatar === 'null' ?
                                            (member.role === 'Sales Manager' ? 'primary.main' : 'secondary.main')
                                            : 'transparent'
                                    }}
                                    imgProps={{
                                        onError: (e) => {
                                            console.error('Avatar load error for', member.name, ':', {
                                                avatar: member.avatar,
                                                url: e.target.src,
                                                error: e.error
                                            });
                                            e.target.onerror = null;
                                            e.target.src = '';
                                        },
                                        onLoad: () => {
                                            console.log('Avatar loaded successfully for', member.name, ':', member.avatar);
                                        }
                                    }}
                                >
                                        {(!member.avatar || member.avatar === 'null') && member.name?.charAt(0).toUpperCase()}
                                    </Avatar>
                                </Badge>
                                    <Box flexGrow={1}>
                                        <Typography variant="subtitle1" fontWeight={600}>
                                            {member.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {member.role || 'Sales Representative'}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={member.status || 'active'}
                                        size="small"
                                        color={member.status === 'active' ? 'success' : 'error'}


                                        sx={{ fontWeight: 500, textTransform: 'capitalize', backgroundColor: member.status === 'active' ? theme.palette.success.light : theme.palette.error.light }}
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Contact Information
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />

                                        <Box
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            flexWrap="wrap"
                                            gap={4}
                                        >
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <EmailIcon color="action" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Email</Typography>
                                                    <Typography variant="body2">{member.email}</Typography>
                                                </Box>
                                            </Box>

                                            <Box display="flex" alignItems="center" gap={1}>
                                                <PhoneIcon color="action" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Phone</Typography>
                                                    <Typography variant="body2">{member.phone || 'Not provided'}</Typography>
                                                </Box>
                                            </Box>

                                            <Box display="flex" alignItems="center" gap={1}>
                                                <BusinessIcon color="action" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Position</Typography>
                                                    <Typography variant="body2">{member.role || 'Sales Representative'}</Typography>
                                                </Box>
                                            </Box>                                                <Box display="flex" alignItems="center" gap={1}>                                                <CalendarIcon color="action" />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">Joined Date</Typography>
                                                    <Typography variant="body2">
                                                        {member.created_date ? new Date(member.created_date).toLocaleDateString() : 'Not available'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={12}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            Recent Contracts
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />
                                        {member.contracts && member.contracts.length > 0 ? (
                                            <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: theme.palette.grey[50] }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Contract ID</TableCell>

                                                            <TableCell sx={{ fontWeight: 600 }}>Customer </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Company </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Cre.Date</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Exp. Date</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Package </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {member.contracts.map(contract => (
                                                            <TableRow key={contract.id} hover>
                                                                <TableCell>{contract.id}</TableCell>
                                                                <TableCell>{contract.customer_name}</TableCell>
                                                                <TableCell>{contract.company_name}</TableCell>
                                                                <TableCell>{contract.email}</TableCell>
                                                                <TableCell>{contract.phone}</TableCell>

                                                                <TableCell>${parseFloat(contract.grand_total).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>                                                                <TableCell>
                                                                    <Chip
                                                                        label={contract.payment_status === 'pending' ? 'Pending' :
                                                                            new Date(contract.expiration_date) < new Date() ? 'Expired' :
                                                                                'Active'
                                                                        }
                                                                        size="small"
                                                                        color={
                                                                            contract.payment_status === 'pending' ? 'warning' :
                                                                                new Date(contract.expiration_date) < new Date() ? 'error' :
                                                                                    'success'
                                                                        }
                                                                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{new Date(contract.created_date).toLocaleDateString()}</TableCell>
                                                                <TableCell>{new Date(contract.expiration_date).toLocaleDateString()}</TableCell>
                                                                <TableCell>{
                                                                    (() => {
                                                                        const pkg = subscriptionPackages.find(p => p.id === contract.subscription_package_id || p._id === contract.subscription_package_id);
                                                                        return pkg ? pkg.package_name : contract.subscription_package_id;
                                                                    })()
                                                                }</TableCell>
                                                                <TableCell>                                                                    <RemoveRedEye
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => window.open(`${process.env.REACT_APP_API_URL}/customer-contracts/${contract.id}/pdf`, '_blank')}
                                                                />

                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        ) : (
                                            <Paper
                                                elevation={0}
                                                sx={{
                                                    p: 3,
                                                    textAlign: 'center',
                                                    backgroundColor: theme.palette.grey[50],
                                                    borderRadius: 1
                                                }}
                                            >
                                                <Typography variant="body2" color="text.secondary">
                                                    No contracts assigned to this team member.
                                                </Typography>
                                            </Paper>
                                        )}
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%', boxShadow: theme.shadows[3] }}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Team;
